import { createSlice } from '@reduxjs/toolkit'
import { API } from 'src/utils/interceptor'
import Sentry from 'src/utils/sentry'
import {
  fetchOllieFindings,
  fetchOllieOpening,
  OLLIE_PLATFORM,
} from 'src/utils/ollieFindingsApi'
import {
  clearOllieInFlight,
  isOllieRequestInFlight,
  shareOllieRequest,
} from 'src/utils/ollieInFlight'
import {
  OLLIE_REFRESH_FOLLOW_UP,
  shouldFollowUpOllieRefresh,
} from 'src/utils/ollieRefreshFollowUp'

export function getOllieApiError(error) {
  const data = error?.response?.data || {}
  const status = error?.response?.status
  const rawMessage = data.message || error?.message || 'Something went wrong'
  const message = /is not defined|referenceerror|syntaxerror|typeerror/i.test(
    rawMessage,
  )
    ? 'Ollie had trouble loading. Please close and try again.'
    : rawMessage

  return {
    status,
    code: data.code || 'OLLIE_ERROR',
    message,
    paywall: data.paywall || null,
    limitType: data.limitType || null,
    plan: data.plan || null,
    usage: data.usage || null,
    limitReached:
      status === 402 || data.code === 'OLLIE_LIMIT_REACHED' || false,
  }
}

/** Plan/usage limits are expected product behaviour — do not send to Sentry. */
export function isExpectedOllieBusinessError(parsed) {
  return Boolean(
    parsed?.limitReached ||
      parsed?.code === 'OLLIE_LIMIT_REACHED' ||
      parsed?.status === 402,
  )
}

const initialState = {
  enabled: false,
  ready: false,
  /** Incremental discoverability entry points (Home / Contextual Ask / intro). */
  discoverabilityEntries: {
    enabled: true,
    cohort: 'all',
  },
  introMessage: '',
  supportFallback: '',
  conversationId: null,
  messages: [],
  starterPrompts: [],
  /** After a successful first free-form reply (Activation 2.2). */
  firstConversationNextSteps: null,
  usage: null,
  usageWarning: null,
  limitReached: false,
  limitType: null,
  paywall: null,
  /** Being at the limit shows a banner; the paywall opens on a send or a tap. */
  paywallRequested: false,
  isLoading: false,
  isLoadingMessages: false,
  isSending: false,
  isSubmittingFeedback: false,
  error: null,
  /** Finding this conversation is about (Express `conversation.finding`). */
  finding: null,
  /** Why? / How? / Show me / Help me / Do it for me for that finding. */
  chips: [],
  /** Last send's `{ quotaExempt, freeLeftToday }` in a finding thread. */
  followUp: null,
  /** Last "not helpful" / report thank-you + mailto to Stephen. */
  feedbackFollowUp: null,
  /** Question Ollie didn't answer (timed out or failed), offered as Retry. */
  lastFailedSend: null,
  findings: {
    venueId: null,
    status: 'idle',
    items: [],
    asOf: null,
    stale: false,
    refreshing: false,
    quiet: true,
    maxVisible: 3,
    loadedAt: null,
    error: null,
  },
  opening: {
    venueId: null,
    pageContext: '',
    status: 'idle',
    data: null,
    error: null,
  },
}

/** Re-read findings after this long; the cache only changes nightly or after an action. */
export const OLLIE_FINDINGS_FRESH_MS = 5 * 60 * 1000

const applyFindingThread = (state, { finding, chips }) => {
  if (finding !== undefined) state.finding = finding || null
  if (chips !== undefined) state.chips = Array.isArray(chips) ? chips : []
}

/** Everything that belongs to one conversation; a new one starts empty. */
const clearThread = state => {
  state.messages = []
  state.finding = null
  state.chips = []
  state.followUp = null
  state.lastFailedSend = null
  state.firstConversationNextSteps = null
}

const ollieSlice = createSlice({
  name: 'ollie',
  initialState,
  reducers: {
    setStatus: (state, { payload }) => {
      state.enabled = Boolean(payload.enabled)
      state.ready = Boolean(payload.ready)
      state.introMessage = payload.introMessage || ''
      state.supportFallback = payload.supportFallback || ''
      if (payload.discoverabilityEntries) {
        state.discoverabilityEntries = {
          enabled: payload.discoverabilityEntries.enabled !== false,
          cohort: payload.discoverabilityEntries.cohort || 'all',
        }
      }
    },
    setStarterPrompts: (state, { payload }) => {
      state.starterPrompts = payload.starterPrompts || []
      if (payload.introMessage) {
        state.introMessage = payload.introMessage
      }
      if (payload.supportFallback) {
        state.supportFallback = payload.supportFallback
      }
      if (payload.usage) {
        state.usage = payload.usage
      }
    },
    setUsage: (state, { payload }) => {
      state.usage = payload.usage || payload
      state.limitReached = Boolean(payload.limitReached)
      if (payload.paywall !== undefined) {
        state.paywall = payload.paywall
      }
    },
    /** Back to what Ollie noticed: drop the old thread before the new one loads. */
    clearOllieThread: state => {
      clearThread(state)
      state.conversationId = null
      state.isSending = false
      state.error = null
    },
    setConversationStart: (state, { payload }) => {
      clearThread(state)
      state.conversationId = payload.conversation?.id || null
      state.starterPrompts = payload.starterPrompts || []
      state.usage = payload.usage || null
      state.limitReached = Boolean(payload.limitReached)
      state.limitType = payload.limitType || null
      state.paywall = payload.paywall || null
      state.error = null

      if (payload.conversation?.introMessage) {
        state.introMessage = payload.conversation.introMessage
      }
      if (payload.conversation?.supportFallback) {
        state.supportFallback = payload.conversation.supportFallback
      }
      applyFindingThread(state, {
        finding: payload.conversation?.finding ?? null,
        chips: payload.chips ?? [],
      })
      if (Array.isArray(payload.messages)) {
        state.messages = payload.messages
      }
      state.followUp = null
      state.lastFailedSend = null
    },
    setMessages: (state, { payload }) => {
      state.messages = payload.messages || []
      state.lastFailedSend = null
      applyFindingThread(state, {
        finding: payload.finding,
        chips: payload.chips,
      })
      if (payload.introMessage) {
        state.introMessage = payload.introMessage
      }
      if (Array.isArray(payload.starterPrompts)) {
        state.starterPrompts = payload.starterPrompts
      }
      if (payload.supportFallback) {
        state.supportFallback = payload.supportFallback
      }
    },
    appendMessages: (state, { payload }) => {
      if (payload.userMessage) {
        state.messages.push(payload.userMessage)
      }
      if (payload.assistantMessage) {
        state.messages.push(payload.assistantMessage)
      }
      if (payload.usage) {
        state.usage = payload.usage
      }
      if (payload.usageWarning !== undefined) {
        state.usageWarning = payload.usageWarning
      }
      state.limitReached = false
      state.limitType = null
      state.paywall = null
      state.error = null
    },
    appendOptimisticUserMessage: (state, { payload }) => {
      state.messages.push({
        id: payload.pendingId,
        role: 'user',
        content: payload.content,
        chipId: payload.chipId || null,
        createdAt: new Date().toISOString(),
        pending: true,
      })
      state.error = null
    },
    confirmSentMessages: (state, { payload }) => {
      const { pendingId, userMessage, assistantMessage, usage, usageWarning } =
        payload
      state.followUp = payload.followUp || null
      if (payload.firstConversationNextSteps) {
        state.firstConversationNextSteps = payload.firstConversationNextSteps
      }

      const shown = message =>
        Boolean(message?.id) &&
        state.messages.some(item => item.id === message.id)
      const pendingIndex = pendingId
        ? state.messages.findIndex(item => item.id === pendingId)
        : -1

      if (userMessage && shown(userMessage)) {
        if (pendingIndex !== -1) state.messages.splice(pendingIndex, 1)
      } else if (pendingIndex !== -1 && userMessage) {
        state.messages[pendingIndex] = userMessage
      } else if (userMessage) {
        state.messages.push(userMessage)
      }

      if (assistantMessage && !shown(assistantMessage)) {
        state.messages.push(assistantMessage)
      }
      if (usage) {
        state.usage = usage
      }
      if (usageWarning !== undefined) {
        state.usageWarning = usageWarning
      }
      // A free finding follow-up at the limit leaves the limit where it was.
      if (!payload.followUp?.quotaExempt) {
        state.limitReached = false
        state.limitType = null
        state.paywall = null
      }
      state.error = null
    },
    removeOptimisticUserMessage: (state, { payload }) => {
      state.messages = state.messages.filter(
        item => item.id !== payload.pendingId,
      )
    },
    setMessageFeedback: (state, { payload }) => {
      const message = state.messages.find(item => item.id === payload.messageId)
      if (message) {
        message.feedback = payload.feedback
      }
      if (payload.followUp !== undefined) {
        state.feedbackFollowUp = payload.followUp || null
      }
    },
    setFeedbackFollowUp: (state, { payload }) => {
      state.feedbackFollowUp = payload || null
    },
    setLastFailedSend: (state, { payload }) => {
      state.lastFailedSend = payload || null
    },
    setFindingsLoading: (state, { payload }) => {
      if (state.findings.venueId !== payload.venueId) {
        state.findings = { ...initialState.findings, venueId: payload.venueId }
      }
      state.findings.status = 'loading'
      state.findings.error = null
    },
    setFindings: (state, { payload }) => {
      state.findings = {
        venueId: payload.venueId,
        status: 'ready',
        items: payload.findings || [],
        asOf: payload.asOf || null,
        stale: Boolean(payload.stale),
        refreshing: Boolean(payload.refreshing),
        quiet: payload.quiet !== false,
        maxVisible: payload.maxVisible ?? 3,
        loadedAt: payload.loadedAt || Date.now(),
        error: null,
      }
    },
    setFindingsError: (state, { payload }) => {
      if (state.findings.venueId !== payload.venueId) return
      state.findings.status = 'error'
      state.findings.error =
        payload.message || 'Unable to load what Ollie noticed'
    },
    /** Snoozed / dismissed / done here: hide now; Express hides it on the next read. */
    removeFinding: (state, { payload }) => {
      state.findings.items = state.findings.items.filter(
        item => item.checkId !== payload.checkId,
      )
      state.findings.quiet = !state.findings.items.some(
        item => item.kind !== 'notice',
      )
    },
    /** Shown in the panel: clear the Ollie button dot without a re-read. */
    markFindingSeen: (state, { payload }) => {
      const item = state.findings.items.find(
        entry => entry.checkId === payload.checkId,
      )
      if (item) item.newForYou = false
    },
    /** After a confirmed action Express refreshes the cache; re-read on next use. */
    markFindingsStale: state => {
      state.findings.loadedAt = null
    },
    setOpeningLoading: (state, { payload }) => {
      state.opening = {
        ...initialState.opening,
        venueId: payload.venueId,
        pageContext: payload.pageContext || '',
        status: 'loading',
      }
    },
    setOpening: (state, { payload }) => {
      if (state.opening.venueId !== payload.venueId) return
      state.opening.status = 'ready'
      state.opening.data = payload.opening
      state.opening.error = null
    },
    setOpeningError: (state, { payload }) => {
      if (state.opening.venueId !== payload.venueId) return
      state.opening.status = 'error'
      state.opening.error = payload.message || null
    },
    setIsLoading: (state, { payload }) => {
      state.isLoading = payload
    },
    setIsLoadingMessages: (state, { payload }) => {
      state.isLoadingMessages = payload
    },
    setIsSending: (state, { payload }) => {
      state.isSending = payload
    },
    setIsSubmittingFeedback: (state, { payload }) => {
      state.isSubmittingFeedback = payload
    },
    setError: (state, { payload }) => {
      state.error = payload
    },
    setLimitState: (state, { payload }) => {
      state.limitReached = Boolean(payload.limitReached)
      state.limitType = payload.limitType || null
      state.paywall = payload.paywall || null
      state.usage = payload.usage || state.usage
      state.error = payload.message || null
      state.paywallRequested = Boolean(payload.requestPaywall)
    },
    setPaywallRequested: (state, { payload }) => {
      state.paywallRequested = Boolean(payload)
    },
    resetOllie: () => ({
      ...initialState,
      messages: [],
      starterPrompts: [],
    }),
  },
})

export const {
  setStatus,
  setStarterPrompts,
  setUsage,
  setConversationStart,
  clearOllieThread,
  setMessages,
  appendMessages,
  appendOptimisticUserMessage,
  confirmSentMessages,
  removeOptimisticUserMessage,
  setMessageFeedback,
  setFeedbackFollowUp,
  setLastFailedSend,
  setFindingsLoading,
  setFindings,
  setFindingsError,
  removeFinding,
  markFindingSeen,
  markFindingsStale,
  setOpeningLoading,
  setOpening,
  setOpeningError,
  setIsLoading,
  setIsLoadingMessages,
  setIsSending,
  setIsSubmittingFeedback,
  setError,
  setLimitState,
  setPaywallRequested,
  resetOllie,
} = ollieSlice.actions

function reportOllieFailure(error, parsed) {
  if (isExpectedOllieBusinessError(parsed)) {
    return
  }

  if (error instanceof Error) {
    Sentry.captureException(error)
    return
  }

  Sentry.captureException(new Error(parsed?.message || 'Ollie request failed'))
}

function failOllieRequest(
  dispatch,
  error,
  { applyLimitState = true, requestPaywall = false } = {},
) {
  const parsed = getOllieApiError(error)

  if (applyLimitState && parsed.limitReached) {
    dispatch(setLimitState({ ...parsed, requestPaywall }))
  } else {
    dispatch(setError(parsed.message))
  }

  reportOllieFailure(error, parsed)
  return { success: false, ...parsed }
}

export const fetchOllieStatus = () => async dispatch => {
  try {
    const data = await API.get('api2', 'ollie/status')
    dispatch(setStatus(data))
    return data
  } catch (error) {
    return failOllieRequest(dispatch, error, { applyLimitState: false })
  }
}

export const fetchOllieStarterPrompts =
  ({ venueId, pageContext = '' }) =>
  async dispatch => {
    try {
      const data = await API.get('api2', 'ollie/starter-prompts', {
        queryStringParameters: {
          venueId,
          pageContext,
        },
      })
      dispatch(setStarterPrompts(data))
      return data
    } catch (error) {
      return failOllieRequest(dispatch, error, { applyLimitState: false })
    }
  }

export const fetchOllieUsage = venueId => async dispatch => {
  try {
    const data = await API.get('api2', `ollie/usage/${venueId}`)
    if (data?.limitReached) {
      dispatch(setLimitState({ ...data, requestPaywall: false }))
    } else {
      dispatch(
        setUsage({
          usage: data.usage,
          limitReached: false,
          paywall: null,
        }),
      )
    }
    return data
  } catch (error) {
    return failOllieRequest(dispatch, error)
  }
}

export const startOllieConversation =
  ({ venueId, pageContext = '', checkId = '' }) =>
  async dispatch => {
    dispatch(setIsLoading(true))
    dispatch(setError(null))

    try {
      const data = await API.post('api2', 'ollie/conversations', {
        body: {
          venueId,
          platform: OLLIE_PLATFORM,
          pageContext,
          ...(checkId ? { finding: { checkId } } : {}),
        },
      })

      dispatch(
        setConversationStart({
          ...data,
          paywall: data.paywall || null,
        }),
      )
      dispatch(setIsLoading(false))
      return data
    } catch (error) {
      dispatch(setIsLoading(false))
      return failOllieRequest(dispatch, error)
    }
  }

export const fetchOllieMessages =
  (conversationId, pageContext = '', venueId = '') =>
  async dispatch => {
    if (!conversationId) {
      return { success: false, message: 'No conversation id' }
    }

    dispatch(setIsLoadingMessages(true))
    dispatch(setError(null))

    try {
      const data = await API.get(
        'api2',
        `ollie/conversations/${conversationId}/messages`,
        {
          queryStringParameters: {
            ...(pageContext ? { pageContext } : {}),
            ...(venueId ? { venueId } : {}),
          },
        },
      )
      dispatch(setMessages(data))
      dispatch(setIsLoadingMessages(false))
      return data
    } catch (error) {
      dispatch(setIsLoadingMessages(false))
      return failOllieRequest(dispatch, error, { applyLimitState: false })
    }
  }

const currentThreadId = getState => getState?.()?.ollie?.conversationId ?? null

export const sendOllieMessage =
  ({
    conversationId,
    message,
    pageContext = '',
    chipId = null,
    venueId = '',
  }) =>
  async (dispatch, getState) => {
    if (!conversationId) {
      const parsed = {
        success: false,
        message: 'No active Ollie conversation',
      }
      dispatch(setError(parsed.message))
      return parsed
    }

    const threadAtSend = currentThreadId(getState)
    const threadMovedOn = () => currentThreadId(getState) !== threadAtSend
    const pendingId = `pending-${Date.now()}`
    const attempt = { conversationId, message, pageContext, chipId, venueId }

    dispatch(
      appendOptimisticUserMessage({ pendingId, content: message, chipId }),
    )
    dispatch(setIsSending(true))
    dispatch(setError(null))
    dispatch(setLastFailedSend(null))

    try {
      const data = await API.post(
        'api2',
        `ollie/conversations/${conversationId}/messages`,
        {
          body: {
            message,
            pageContext,
            ...(chipId ? { chipId } : {}),
            ...(venueId ? { venueId } : {}),
          },
        },
      )

      if (threadMovedOn()) {
        dispatch(setIsSending(false))
        return { ...data, staleConversation: true }
      }

      dispatch(
        confirmSentMessages({
          pendingId,
          userMessage: data.userMessage,
          assistantMessage: data.assistantMessage,
          usage: data.usage,
          usageWarning: data.usageWarning,
          followUp: data.followUp,
          firstConversationNextSteps: data.firstConversationNextSteps || null,
        }),
      )
      if (data.assistantMessage?.timedOut) {
        dispatch(setLastFailedSend({ ...attempt, reason: 'timeout' }))
      }
      dispatch(setIsSending(false))
      return data
    } catch (error) {
      dispatch(removeOptimisticUserMessage({ pendingId }))
      dispatch(setIsSending(false))
      if (threadMovedOn()) {
        return { success: false, staleConversation: true }
      }
      const failed = failOllieRequest(dispatch, error, { requestPaywall: true })
      if (isRetryableOllieSendFailure(failed)) {
        dispatch(setLastFailedSend({ ...attempt, reason: 'error' }))
      }
      return failed
    }
  }

/**
 * Pure. Ollie's reply to an answered Why? in a reopened same-day thread,
 * so the card shows that answer instead of asking (and charging) again.
 */
export const findAnsweredOllieWhy = (messages = []) => {
  const list = Array.isArray(messages) ? messages : []
  for (let index = list.length - 1; index >= 0; index -= 1) {
    const message = list[index]
    if (
      message?.role !== 'user' ||
      message.chipId !== 'why' ||
      message.unanswered
    ) {
      continue
    }
    const reply = list.slice(index + 1).find(next => next?.role === 'assistant')
    if (reply && !reply.timedOut) return reply
  }
  return null
}

/**
 * Card / noticed-row "Why?": once the finding's thread is open, ask its
 * Why? chip straight away, the same message a chip tap sends (so a Free
 * finding follow-up stays free). No Why? chip, nothing is sent.
 */
export const askOllieFindingWhy =
  ({ start, pageContext = '', venueId = '' }) =>
  dispatch => {
    const conversationId = start?.conversation?.id
    const answered = conversationId
      ? findAnsweredOllieWhy(start?.messages)
      : null
    if (answered) {
      return {
        success: true,
        replayed: true,
        conversationId,
        assistantMessage: answered,
      }
    }
    const chip = (start?.chips || []).find(item => item?.id === 'why')
    if (!conversationId || !chip) return null
    return dispatch(
      sendOllieMessage({
        conversationId,
        message: chip.question || chip.label || 'Why?',
        pageContext,
        chipId: chip.id,
        venueId,
      }),
    )
  }

/** Network drop or server error: worth a Retry. Limits and bad input aren't. */
export const isRetryableOllieSendFailure = failed =>
  !failed?.limitReached && (!failed?.status || failed.status >= 500)

/** Send the question Ollie didn't answer again, in the same conversation. */
export const retryOllieSend = () => (dispatch, getState) => {
  const failed = getState().ollie?.lastFailedSend
  if (!failed?.conversationId || !failed.message) return null
  return dispatch(
    sendOllieMessage({
      conversationId: failed.conversationId,
      message: failed.message,
      pageContext: failed.pageContext || '',
      chipId: failed.chipId || null,
      venueId: failed.venueId || '',
    }),
  )
}

export const submitOllieFeedback =
  ({ conversationId, messageId, rating, comment = '', reason = null }) =>
  async dispatch => {
    dispatch(setIsSubmittingFeedback(true))
    dispatch(setError(null))

    try {
      const data = await API.post(
        'api2',
        `ollie/conversations/${conversationId}/messages/${messageId}/feedback`,
        {
          body: {
            rating,
            comment,
            ...(reason ? { reason } : {}),
          },
        },
      )

      dispatch(
        setMessageFeedback({
          messageId: data.messageId,
          feedback: data.feedback,
          followUp: data.followUp ?? null,
        }),
      )
      dispatch(setIsSubmittingFeedback(false))
      return data
    } catch (error) {
      dispatch(setIsSubmittingFeedback(false))
      return failOllieRequest(dispatch, error, { applyLimitState: false })
    }
  }

const pendingFindingsFollowUps = new Map()

const findingsRequestKey = venueId => `findings:${OLLIE_PLATFORM}:${venueId}`

/** Cancel queued re-reads and forget in-flight reads (tests; leaving a venue). */
export const clearOllieFindingsFollowUps = () => {
  pendingFindingsFollowUps.forEach(timer => clearTimeout(timer))
  pendingFindingsFollowUps.clear()
  clearOllieInFlight()
}

/**
 * Load this person's findings for a venue. Skips the request when the store
 * already holds a fresh read for the venue, unless `force`. While Express
 * says it is refreshing, reads again quietly (capped), so a fix made outside
 * Ollie or an action just confirmed shows without a reload.
 *
 * Callers that ask while a read for the venue is in flight share it (one
 * request); only the caller that started it stores the result.
 */
export const loadOllieFindings =
  ({
    venueId,
    force = false,
    now = Date.now(),
    followUp = 0,
    quiet: quietRequested = false,
  }) =>
  async (dispatch, getState) => {
    if (!venueId) return { success: false, message: 'No venue' }
    const current = getState().ollie?.findings
    const fresh =
      current?.venueId === venueId &&
      current.status === 'ready' &&
      current.loadedAt &&
      now - current.loadedAt < OLLIE_FINDINGS_FRESH_MS
    if (fresh && !force) return { success: true, cached: true, ...current }
    const key = findingsRequestKey(venueId)
    if (
      current?.venueId === venueId &&
      current.status === 'loading' &&
      !force &&
      !isOllieRequestInFlight(key)
    ) {
      return { success: true, pending: true }
    }

    const { promise, shared } = shareOllieRequest(
      key,
      () => fetchOllieFindings(venueId),
      { force },
    )
    if (shared) {
      const showLoading =
        current?.venueId !== venueId ||
        !['loading', 'ready'].includes(current.status)
      if (showLoading) dispatch(setFindingsLoading({ venueId }))
      try {
        return { success: true, shared: true, ...(await promise) }
      } catch (error) {
        const parsed = getOllieApiError(error)
        if (showLoading) {
          dispatch(setFindingsError({ venueId, message: parsed.message }))
        }
        return { success: false, shared: true, ...parsed }
      }
    }

    const quiet =
      (quietRequested || followUp > 0) && current?.venueId === venueId
    if (!quiet) dispatch(setFindingsLoading({ venueId }))
    try {
      const data = await promise
      dispatch(setFindings({ venueId, loadedAt: now, ...data }))
      if (
        shouldFollowUpOllieRefresh({ refreshing: data.refreshing, followUp }) &&
        !pendingFindingsFollowUps.has(venueId)
      ) {
        const timer = setTimeout(() => {
          pendingFindingsFollowUps.delete(venueId)
          dispatch(
            loadOllieFindings({
              venueId,
              force: true,
              now: Date.now(),
              followUp: followUp + 1,
            }),
          )
        }, OLLIE_REFRESH_FOLLOW_UP.delayMs)
        pendingFindingsFollowUps.set(venueId, timer)
      }
      return { success: true, ...data }
    } catch (error) {
      const parsed = getOllieApiError(error)
      if (!quiet) {
        dispatch(setFindingsError({ venueId, message: parsed.message }))
      }
      reportOllieFailure(error, parsed)
      return { success: false, ...parsed }
    }
  }

/** Opening message for the panel; falls back to the static intro on failure. */
export const loadOllieOpening =
  ({ venueId, pageContext = '' }) =>
  async dispatch => {
    if (!venueId) return { success: false, message: 'No venue' }
    dispatch(setOpeningLoading({ venueId, pageContext }))
    try {
      const opening = await fetchOllieOpening({ venueId, pageContext })
      dispatch(setOpening({ venueId, opening }))
      return { success: true, opening }
    } catch (error) {
      const parsed = getOllieApiError(error)
      dispatch(setOpeningError({ venueId, message: parsed.message }))
      reportOllieFailure(error, parsed)
      return { success: false, ...parsed }
    }
  }

export default ollieSlice
